import React, { useEffect, useState, useCallback, useRef } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Copy,
  GitMerge,
  X,
  Trash2,
  RotateCw,
  CheckCircle2,
  ExternalLink,
  ArrowRight,
  Search,
} from "lucide-react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { DUPLICATES_COUNT_QUERY_KEY } from "@/lib/queryKeys";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface DupRecipe {
  id: number;
  title: string;
  authorSource: string | null;
  category: string | null;
  cuisine: string | null;
  yieldServings: string | null;
  totalTime: string | null;
  directions: string;
  validationStatus: string;
  handwrittenNotes: string | null;
  createdAt: string | null;
  ingredients: Array<{
    id: number;
    item: string;
    amount: string | null;
    unit: string | null;
    prepNotes: string | null;
    sectionHeader: string | null;
  }>;
  sourceImages: Array<{
    id: number;
    url: string;
    originalName: string;
    isPrimary: boolean;
  }>;
}

interface DupPair {
  id: number;
  score: number;
  status: string;
  createdAt: string;
  resolvedAt: string | null;
  recipeA: DupRecipe;
  recipeB: DupRecipe;
}

function getBaseUrl(): string {
  return import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
}

const STATUS_LABELS: Record<string, string> = {
  validated: "Validated",
  needs_correction: "Needs Correction",
  unreviewed: "Unreviewed",
};
const STATUS_COLORS: Record<string, string> = {
  validated: "bg-green-100 text-green-800 border-green-200",
  needs_correction: "bg-orange-100 text-orange-800 border-orange-200",
  unreviewed: "bg-gray-100 text-gray-700 border-gray-200",
};

function scoreColor(score: number): string {
  if (score >= 0.95) return "bg-red-100 text-red-800 border-red-200";
  if (score >= 0.9) return "bg-orange-100 text-orange-800 border-orange-200";
  return "bg-amber-100 text-amber-800 border-amber-200";
}

function formatScore(score: number): string {
  return `${Math.round(score * 100)}% similar`;
}

// Levenshtein-based normalized similarity, mirrors the server-side
// helper in extraction.ts. Returns 0..1.
function titleSimilarity(a: string, b: string): number {
  const s1 = (a ?? "").toLowerCase().trim();
  const s2 = (b ?? "").toLowerCase().trim();
  if (s1 === s2) return 1;
  if (s1.length === 0 || s2.length === 0) return 0;
  const m = s1.length;
  const n = s2.length;
  const dp: number[] = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const temp = dp[j];
      dp[j] =
        s1[i - 1] === s2[j - 1]
          ? prev
          : 1 + Math.min(prev, dp[j], dp[j - 1]);
      prev = temp;
    }
  }
  return 1 - dp[n] / Math.max(m, n);
}

// Normalize an ingredient item name into a comparable key. We strip
// punctuation, collapse whitespace, and drop common qualifiers so
// "fresh ginger root" and "ginger root, fresh" hash the same.
function normalizeItem(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\b(fresh|dried|ground|chopped|minced|melted|cooled|softened|optional|to taste)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Compare two recipes' ingredient lists. Section-header rows (no item)
// are skipped. Returns { sharedKeys, onlyA, onlyB, totalUnique, jaccard }.
function compareIngredients(
  a: DupRecipe["ingredients"],
  b: DupRecipe["ingredients"],
): {
  sharedKeys: Set<string>;
  shared: number;
  onlyA: number;
  onlyB: number;
  totalUnique: number;
  jaccard: number;
} {
  const keysA = new Set<string>();
  const keysB = new Set<string>();
  for (const ing of a) {
    const k = normalizeItem(ing.item);
    if (k) keysA.add(k);
  }
  for (const ing of b) {
    const k = normalizeItem(ing.item);
    if (k) keysB.add(k);
  }
  const sharedKeys = new Set<string>();
  for (const k of keysA) if (keysB.has(k)) sharedKeys.add(k);
  const onlyA = keysA.size - sharedKeys.size;
  const onlyB = keysB.size - sharedKeys.size;
  const totalUnique = keysA.size + keysB.size - sharedKeys.size;
  // Two empty ingredient lists are vacuously identical (no items to
  // disagree about), so treat as 100% overlap rather than 0%.
  const jaccard = totalUnique === 0 ? 1 : sharedKeys.size / totalUnique;
  return {
    sharedKeys,
    shared: sharedKeys.size,
    onlyA,
    onlyB,
    totalUnique,
    jaccard,
  };
}

// Per-field diff flags: which side-by-side metadata fields disagree.
// "differs" means both sides have a value AND they're not equal (case-
// insensitive trim), OR one side has a value the other lacks.
function diffField(
  va: string | null | undefined,
  vb: string | null | undefined,
): boolean {
  const a = (va ?? "").trim().toLowerCase();
  const b = (vb ?? "").trim().toLowerCase();
  if (a === b) return false;
  return true;
}

interface PairDiff {
  titleSim: number;
  ingredients: ReturnType<typeof compareIngredients>;
  fields: {
    title: boolean;
    category: boolean;
    cuisine: boolean;
    yieldServings: boolean;
    totalTime: boolean;
    authorSource: boolean;
    directions: boolean;
  };
}

function computePairDiff(a: DupRecipe, b: DupRecipe): PairDiff {
  return {
    titleSim: titleSimilarity(a.title, b.title),
    ingredients: compareIngredients(a.ingredients, b.ingredients),
    fields: {
      title: diffField(a.title, b.title),
      category: diffField(a.category, b.category),
      cuisine: diffField(a.cuisine, b.cuisine),
      yieldServings: diffField(a.yieldServings, b.yieldServings),
      totalTime: diffField(a.totalTime, b.totalTime),
      authorSource: diffField(a.authorSource, b.authorSource),
      directions: diffField(a.directions, b.directions),
    },
  };
}

// Pill style for an inline percentage breakdown (title vs ingredients).
function breakdownColor(pct: number): string {
  if (pct >= 0.85) return "bg-red-50 text-red-700 border-red-200";
  if (pct >= 0.6) return "bg-orange-50 text-orange-700 border-orange-200";
  if (pct >= 0.3) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-slate-50 text-slate-600 border-slate-200";
}

function formatDate(d: string | null): string {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function DuplicatesPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const [statusFilter, setStatusFilter] = useState<
    "pending" | "dismissed" | "all"
  >("pending");
  const [pairs, setPairs] = useState<DupPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyPairId, setBusyPairId] = useState<number | null>(null);
  const [scanning, setScanning] = useState(false);
  // Synchronous double-click guard (busyPairId only blocks after re-render).
  const inFlightRef = useRef<Set<number>>(new Set());
  const [confirmAction, setConfirmAction] = useState<{
    pair: DupPair;
    kind: "mergeIntoA" | "mergeIntoB" | "deleteA" | "deleteB";
  } | null>(null);

  const fetchPairs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${getBaseUrl()}/api/duplicates?status=${statusFilter}`,
      );
      if (!r.ok) {
        throw new Error(`HTTP ${r.status}`);
      }
      const data = (await r.json()) as { duplicates: DupPair[] };
      setPairs(data.duplicates ?? []);
    } catch {
      setPairs([]);
      toast({
        title: "Couldn't load duplicates",
        description: "Try refreshing the page.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);

  useEffect(() => {
    void fetchPairs();
  }, [fetchPairs]);

  const handleDismiss = async (pair: DupPair) => {
    if (inFlightRef.current.has(pair.id)) return;
    inFlightRef.current.add(pair.id);
    setBusyPairId(pair.id);
    try {
      const r = await fetch(
        `${getBaseUrl()}/api/duplicates/${pair.id}/dismiss`,
        { method: "POST" },
      );
      if (!r.ok) throw new Error("Dismiss failed");
      toast({
        title: "Marked as not a duplicate",
        description: `"${pair.recipeA.title}" and "${pair.recipeB.title}" won't be flagged again.`,
      });
      void queryClient.invalidateQueries({ queryKey: DUPLICATES_COUNT_QUERY_KEY });
      await fetchPairs();
    } catch {
      toast({ title: "Dismiss failed", variant: "destructive" });
    } finally {
      inFlightRef.current.delete(pair.id);
      setBusyPairId(null);
    }
  };

  const handleMerge = async (pair: DupPair, keepId: number, dropId: number) => {
    if (inFlightRef.current.has(pair.id)) return;
    inFlightRef.current.add(pair.id);
    setBusyPairId(pair.id);
    try {
      const r = await fetch(
        `${getBaseUrl()}/api/recipes/${keepId}/merge/${dropId}`,
        { method: "POST" },
      );
      if (!r.ok) {
        const err = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? "Merge failed");
      }
      toast({
        title: "Recipes merged",
        description: `Combined into recipe #${keepId}. The duplicate was removed.`,
      });
      setConfirmAction(null);
      void queryClient.invalidateQueries({ queryKey: DUPLICATES_COUNT_QUERY_KEY });
      await fetchPairs();
    } catch (err) {
      toast({
        title: "Merge failed",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      inFlightRef.current.delete(pair.id);
      setBusyPairId(null);
    }
  };

  const handleDelete = async (pair: DupPair, recipeId: number) => {
    if (inFlightRef.current.has(pair.id)) return;
    inFlightRef.current.add(pair.id);
    setBusyPairId(pair.id);
    try {
      const r = await fetch(`${getBaseUrl()}/api/recipes/${recipeId}`, {
        method: "DELETE",
      });
      if (!r.ok) {
        const err = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? "Delete failed");
      }
      toast({
        title: "Recipe deleted",
        description: `Recipe #${recipeId} was removed.`,
      });
      setConfirmAction(null);
      void queryClient.invalidateQueries({ queryKey: DUPLICATES_COUNT_QUERY_KEY });
      await fetchPairs();
    } catch (err) {
      toast({
        title: "Delete failed",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      inFlightRef.current.delete(pair.id);
      setBusyPairId(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-3xl font-serif font-bold text-foreground flex items-center gap-2">
              <Copy className="w-7 h-7 text-primary" />
              Duplicate Review
            </h2>
            <p className="text-muted-foreground mt-1">
              {statusFilter === "pending"
                ? "Recipes the system thinks might be the same dish — review and resolve."
                : `Showing ${statusFilter} duplicate flags.`}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                if (scanning) return;
                setScanning(true);
                try {
                  const r = await fetch(`${getBaseUrl()}/api/duplicates/scan`, {
                    method: "POST",
                  });
                  if (!r.ok) throw new Error(`HTTP ${r.status}`);
                  const data = (await r.json()) as {
                    recipesScanned: number;
                    pairsConsidered: number;
                    pairsMatched: number;
                    threshold: number;
                  };
                  toast({
                    title: "Scan complete",
                    description: `Checked ${data.pairsConsidered.toLocaleString()} pairs across ${data.recipesScanned} recipes. ${data.pairsMatched} pair${data.pairsMatched === 1 ? "" : "s"} matched ≥${Math.round(data.threshold * 100)}% (previously dismissed pairs are skipped).`,
                  });
                  void queryClient.invalidateQueries({
                    queryKey: DUPLICATES_COUNT_QUERY_KEY,
                  });
                  await fetchPairs();
                } catch (err) {
                  toast({
                    title: "Scan failed",
                    description: err instanceof Error ? err.message : String(err),
                    variant: "destructive",
                  });
                } finally {
                  setScanning(false);
                }
              }}
              disabled={scanning || loading}
              title="Compare every recipe title against every other and flag close matches."
            >
              <Search className={`w-4 h-4 mr-2 ${scanning ? "animate-pulse" : ""}`} />
              {scanning ? "Scanning…" : "Scan all recipes"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => fetchPairs()} disabled={loading}>
              <RotateCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          {(["pending", "dismissed", "all"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                statusFilter === s
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-muted-foreground border-border hover:bg-muted"
              }`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {loading && pairs.length === 0 && (
          <Card>
            <CardContent className="py-12 flex flex-col items-center justify-center text-muted-foreground">
              <RotateCw className="w-8 h-8 animate-spin mb-3" />
              <p>Loading duplicates…</p>
            </CardContent>
          </Card>
        )}

        {!loading && pairs.length === 0 && (
          <Card>
            <CardContent className="py-12 flex flex-col items-center justify-center text-center">
              <CheckCircle2 className="w-10 h-10 text-green-500 mb-3" />
              <p className="font-medium text-lg text-foreground">
                {statusFilter === "pending"
                  ? "No pending duplicates"
                  : `No ${statusFilter} flags`}
              </p>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                {statusFilter === "pending"
                  ? "When the AI extracts a recipe whose title closely matches one already in the cookbook, it'll show up here for review."
                  : "Switch filters to see other duplicate-flag history."}
              </p>
            </CardContent>
          </Card>
        )}

        <div className="space-y-4">
          {pairs.map((pair) => {
            const diff = computePairDiff(pair.recipeA, pair.recipeB);
            return (
            <Card
              key={pair.id}
              className={
                pair.status === "pending"
                  ? "border-amber-200/60"
                  : "opacity-75"
              }
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={scoreColor(pair.score)}>
                      {formatScore(pair.score)}
                    </Badge>
                    <Badge
                      variant="outline"
                      className={`text-xs ${breakdownColor(diff.titleSim)}`}
                      title="Title similarity (Levenshtein)"
                    >
                      Title {Math.round(diff.titleSim * 100)}%
                    </Badge>
                    {diff.ingredients.totalUnique === 0 ? (
                      <Badge
                        variant="outline"
                        className="text-xs bg-slate-100 text-slate-600"
                        title="Neither recipe has comparable ingredient items"
                      >
                        Items n/a
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className={`text-xs ${breakdownColor(diff.ingredients.jaccard)}`}
                        title={`${diff.ingredients.shared} shared, ${diff.ingredients.onlyA} only on A, ${diff.ingredients.onlyB} only on B (Jaccard overlap of normalized item names; amounts and units are not compared)`}
                      >
                        Items {diff.ingredients.shared}/
                        {diff.ingredients.totalUnique} (
                        {Math.round(diff.ingredients.jaccard * 100)}%)
                        {(diff.ingredients.onlyA > 0 ||
                          diff.ingredients.onlyB > 0) && (
                          <span className="ml-1 opacity-75">
                            · −{diff.ingredients.onlyA}A / −
                            {diff.ingredients.onlyB}B
                          </span>
                        )}
                      </Badge>
                    )}
                    {pair.status !== "pending" && (
                      <Badge
                        variant="outline"
                        className="bg-slate-100 text-slate-600"
                      >
                        {pair.status}
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      Flagged {formatDate(pair.createdAt)}
                    </span>
                  </div>
                  {pair.status === "pending" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDismiss(pair)}
                      disabled={busyPairId === pair.id}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4 mr-1.5" />
                      Not a duplicate
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <RecipeCompareCard
                    recipe={pair.recipeA}
                    side="A"
                    diff={diff}
                    onView={() => navigate(`/recipe/${pair.recipeA.id}`)}
                  />
                  <RecipeCompareCard
                    recipe={pair.recipeB}
                    side="B"
                    diff={diff}
                    onView={() => navigate(`/recipe/${pair.recipeB.id}`)}
                  />
                </div>

                {pair.status === "pending" && (
                  <div className="mt-5 pt-5 border-t border-border flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between flex-wrap">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() =>
                          setConfirmAction({ pair, kind: "mergeIntoA" })
                        }
                        disabled={busyPairId === pair.id}
                        title={`Keep recipe A (#${pair.recipeA.id}); B is deleted and its missing fields copied over.`}
                      >
                        <GitMerge className="w-4 h-4 mr-1.5" />
                        Merge into A
                        <ArrowRight className="w-3 h-3 mx-1 opacity-60" />
                        keep #{pair.recipeA.id}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          setConfirmAction({ pair, kind: "mergeIntoB" })
                        }
                        disabled={busyPairId === pair.id}
                        title={`Keep recipe B (#${pair.recipeB.id}); A is deleted and its missing fields copied over.`}
                      >
                        <GitMerge className="w-4 h-4 mr-1.5" />
                        Merge into B
                        <ArrowRight className="w-3 h-3 mx-1 opacity-60" />
                        keep #{pair.recipeB.id}
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setConfirmAction({ pair, kind: "deleteA" })
                        }
                        disabled={busyPairId === pair.id}
                        className="text-destructive border-destructive/40 hover:bg-destructive/5"
                      >
                        <Trash2 className="w-4 h-4 mr-1.5" />
                        Delete A
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setConfirmAction({ pair, kind: "deleteB" })
                        }
                        disabled={busyPairId === pair.id}
                        className="text-destructive border-destructive/40 hover:bg-destructive/5"
                      >
                        <Trash2 className="w-4 h-4 mr-1.5" />
                        Delete B
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })}
        </div>
      </div>

      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={(o) => !o && setConfirmAction(null)}
      >
        <AlertDialogContent>
          {confirmAction && (
            <>
              {(() => {
                const { pair, kind } = confirmAction;
                const isMerge = kind === "mergeIntoA" || kind === "mergeIntoB";
                const keeper = kind === "mergeIntoA" ? pair.recipeA : pair.recipeB;
                const dropped = kind === "mergeIntoA" ? pair.recipeB : pair.recipeA;
                const deleting =
                  kind === "deleteA" ? pair.recipeA : kind === "deleteB" ? pair.recipeB : null;
                return (
                  <>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {isMerge
                          ? `Merge into "${keeper.title}"?`
                          : `Delete "${deleting!.title}"?`}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {isMerge
                          ? `Recipe #${dropped.id} "${dropped.title}" will be deleted, and any of its data that recipe #${keeper.id} is missing will be copied over. Source images move to the kept recipe. This cannot be undone.`
                          : "This recipe and its ingredients will be permanently deleted. Source images stay in your library. This cannot be undone."}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => {
                          if (kind === "mergeIntoA")
                            void handleMerge(pair, pair.recipeA.id, pair.recipeB.id);
                          else if (kind === "mergeIntoB")
                            void handleMerge(pair, pair.recipeB.id, pair.recipeA.id);
                          else if (kind === "deleteA")
                            void handleDelete(pair, pair.recipeA.id);
                          else void handleDelete(pair, pair.recipeB.id);
                        }}
                        className="bg-destructive hover:bg-destructive/90"
                      >
                        {isMerge ? "Merge" : "Delete"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </>
                );
              })()}
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

// Tailwind class applied to a metadata field whose value disagrees with
// the other side. Subtle amber wash + outline so it reads as "look here"
// without screaming.
const FIELD_DIFF_CLASS =
  "bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5";

function RecipeCompareCard({
  recipe,
  side,
  diff,
  onView,
}: {
  recipe: DupRecipe;
  side: "A" | "B";
  diff: PairDiff;
  onView: () => void;
}) {
  const primaryImage =
    recipe.sourceImages.find((i) => i.isPrimary) ?? recipe.sourceImages[0];

  // Show all ingredients (not just first 8) so admins can see every diff
  // marker. The list scrolls inside its own max-h container.
  const sharedKeys = diff.ingredients.sharedKeys;

  return (
    <div className="rounded-xl border border-border bg-muted/20 overflow-hidden flex flex-col">
      <div className="px-4 py-2 bg-background border-b border-border flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold">
            {side}
          </span>
          <span className="text-xs text-muted-foreground font-mono">
            #{recipe.id}
          </span>
          <Badge
            variant="outline"
            className={`text-xs ${
              STATUS_COLORS[recipe.validationStatus] ?? STATUS_COLORS.unreviewed
            }`}
          >
            {STATUS_LABELS[recipe.validationStatus] ?? recipe.validationStatus}
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={onView}
        >
          <ExternalLink className="w-3 h-3 mr-1" />
          Open
        </Button>
      </div>
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div>
          <h3
            className={`font-serif font-bold text-lg leading-tight text-foreground inline-block ${
              diff.fields.title ? FIELD_DIFF_CLASS : ""
            }`}
          >
            {recipe.title}
          </h3>
          <div className="flex flex-wrap gap-x-2 gap-y-1 mt-1 text-xs text-muted-foreground">
            {recipe.category && (
              <span className={diff.fields.category ? FIELD_DIFF_CLASS : ""}>
                {recipe.category}
              </span>
            )}
            {recipe.cuisine && (
              <span className={diff.fields.cuisine ? FIELD_DIFF_CLASS : ""}>
                {recipe.cuisine}
              </span>
            )}
            {recipe.yieldServings && (
              <span
                className={diff.fields.yieldServings ? FIELD_DIFF_CLASS : ""}
              >
                Yield: {recipe.yieldServings}
              </span>
            )}
            {recipe.totalTime && (
              <span className={diff.fields.totalTime ? FIELD_DIFF_CLASS : ""}>
                {recipe.totalTime}
              </span>
            )}
            {recipe.authorSource && (
              <span
                className={diff.fields.authorSource ? FIELD_DIFF_CLASS : ""}
              >
                by {recipe.authorSource}
              </span>
            )}
          </div>
        </div>

        {primaryImage && (
          <div className="rounded-lg overflow-hidden border border-border bg-white">
            <img
              src={primaryImage.url}
              alt={primaryImage.originalName}
              className="w-full h-32 object-cover"
              loading="lazy"
            />
          </div>
        )}

        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-3 flex-wrap">
            <span>Ingredients ({recipe.ingredients.length})</span>
            <span className="text-[10px] font-normal text-muted-foreground/70 flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-300" />
              unique
            </span>
            <span className="text-[10px] font-normal text-muted-foreground/70 flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" />
              shared
            </span>
          </p>
          <ul className="text-xs space-y-0.5 max-h-40 overflow-y-auto pr-1">
            {recipe.ingredients.map((ing) => {
              const key = normalizeItem(ing.item);
              // Rows with no comparable item key (e.g. pure section
              // headers or unparseable entries) are excluded from the
              // diff and rendered neutrally — no dot, no tooltip claim.
              const comparable = key !== "";
              const isShared = comparable && sharedKeys.has(key);
              const isUnique = comparable && !isShared;
              return (
                <li
                  key={ing.id}
                  className={`flex gap-1.5 items-start rounded px-1 ${
                    isUnique ? "bg-amber-50 text-amber-900" : "text-foreground/80"
                  }`}
                  title={
                    isUnique
                      ? "Only on this side"
                      : isShared
                        ? "Also on the other side"
                        : undefined
                  }
                >
                  <span
                    className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
                      isUnique
                        ? "bg-amber-400"
                        : isShared
                          ? "bg-emerald-400"
                          : "bg-transparent"
                    }`}
                  />
                  <span>
                    <span className="font-medium">
                      {[ing.amount, ing.unit].filter(Boolean).join(" ")}
                    </span>{" "}
                    {ing.item}
                    {ing.sectionHeader && (
                      <span className="text-muted-foreground italic">
                        {" "}
                        — {ing.sectionHeader}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">
            Directions{" "}
            {diff.fields.directions && (
              <span className="text-amber-700 font-normal">
                · differ from {side === "A" ? "B" : "A"}
              </span>
            )}
          </p>
          <p
            className={`text-xs text-foreground/70 line-clamp-4 whitespace-pre-line ${
              diff.fields.directions ? FIELD_DIFF_CLASS : ""
            }`}
          >
            {recipe.directions || (
              <span className="italic text-muted-foreground">No directions</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
